import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useSearchParams } from 'react-router'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import {
  formatDateValue,
  parseIsoDate,
  startOfTodayJst,
} from '~/models/dateTime'
import {
  DEFAULT_DETAIL_FILTER,
  type DetailFilterState,
} from '~/pages/BalanceDemandDetail/constants'
import {
  type DetailUrlState,
  hiddenTokensOf,
  resolveDetailUrlState,
  writeDetailUrlState,
} from '~/pages/BalanceDemandDetail/models/detailUrlState'
import {
  DETAIL_FILTERS_STORAGE_KEY,
  type StoredDetailFilters,
  deserializeDetailFilters,
  fallbackForArea,
  serializeDetailFilters,
  withAreaBgSelection,
  withToggles,
} from '~/pages/BalanceDemandDetail/models/persistedFilters'

function readStored(): StoredDetailFilters {
  return deserializeDetailFilters(
    getLocalStorageItem(DETAIL_FILTERS_STORAGE_KEY),
  )
}

function writeStored(stored: StoredDetailFilters): void {
  setLocalStorageItem(
    DETAIL_FILTERS_STORAGE_KEY,
    serializeDetailFilters(stored),
  )
}

/** URL・保存値に載せない項目 (需要グループ・料金プラン)。この画面にいる間だけ持つ。 */
type PageOnlyFilter = Pick<DetailFilterState, 'demandGroup' | 'ratePlan'>

function hasToggleChange(a: DetailFilterState, b: DetailFilterState): boolean {
  return JSON.stringify(hiddenTokensOf(a)) !== JSON.stringify(hiddenTokensOf(b))
}

export type DetailFilterStateResult = {
  displayDate: Date
  filter: DetailFilterState
  /** 利用者の日付操作。履歴に積む。 */
  changeDate: (date: Date) => void
  goToToday: () => void
  /** 利用者のフィルター・トグル操作。BG・小売は履歴に積み、トグルは積まない。 */
  changeFilter: (next: DetailFilterState) => void
  /** 選択肢外の値を既定へ戻す補正。履歴に積まず、保存もしない。 */
  correctFilter: (next: DetailFilterState) => void
}

/**
 * 需要詳細の選択状態 (表示日・BG・小売・系列トグル) を URL クエリを正として持つ。
 *
 * React state に持って URL と双方向に同期すると、ブラウザの戻る/進むで state と
 * URL がずれる窓ができ、保存値も含めた三重管理になる。URL から導出すれば戻る/進む
 * は URL の変化だけで再描画される。
 *
 * 初回は URL に無い項目を保存値 → 既定で埋めた状態で描き、同じ値で URL を
 * replace して正準形にする。URL を先に書いてから描くと、既定値での request が
 * 1 回余計に飛び表示が「すべて」から保存値へ跳ねる。
 *
 * 保存は利用者の操作だけ、操作した項目の単位で書く。補正まで保存すると提出の無い
 * 過去日へ移っただけで保存した BG が消え、まとめて保存すると補正で「すべて」に
 * 戻った BG がトグル操作のついでに保存される。共有 URL を開いただけ・戻る/進む
 * でも書かない (自分の保存値を他人の選択で上書きしない)。
 */
export function useDetailFilterState(
  areaCode: AreaCode,
): DetailFilterStateResult {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.toString()
  const todayValue = formatDateValue(startOfTodayJst())
  // URL から導出する値だけで filter を組むと、FilterCard が渡した需要グループ・
  // 料金プランが次の描画で既定値に戻り、選んでも何も起きない。
  const [pageOnly, setPageOnly] = useState<PageOnlyFilter>({
    demandGroup: DEFAULT_DETAIL_FILTER.demandGroup,
    ratePlan: DEFAULT_DETAIL_FILTER.ratePlan,
  })

  const { displayDate, filter } = useMemo(() => {
    const params = new URLSearchParams(search)
    // todayValue は自分で生成した値なので必ず parse できる。
    const today = parseIsoDate(todayValue) ?? startOfTodayJst()
    const { displayDate: date, ...selection } = resolveDetailUrlState(
      params,
      today,
      fallbackForArea(readStored(), areaCode),
    )
    const resolved: DetailFilterState = {
      ...DEFAULT_DETAIL_FILTER,
      ...pageOnly,
      ...selection,
    }
    return { displayDate: date, filter: resolved }
  }, [search, todayValue, areaCode, pageOnly])

  const canonicalSearch = writeDetailUrlState(new URLSearchParams(search), {
    ...filter,
    displayDate,
  }).toString()
  useEffect(() => {
    if (canonicalSearch === search) return
    setSearchParams(new URLSearchParams(canonicalSearch), { replace: true })
  }, [canonicalSearch, search, setSearchParams])

  // 補正 (correctFilter) を書いてから、その描画が確定するまでの BG・小売。router は
  // URL を先に書き換え、描画は transition で後から確定するので、その間の操作は
  // 描画済みの (補正前の) filter から URL を組む。画面には応答の時点で「すべて」が
  // 出ているのに、選択肢外の値を次の日付へ持ち越してしまう (F6 / CI で検出)。
  // navigate の flushSync で同期化しないのは、本体の RouterProvider が flushSync を
  // 持たず黙って無視するため (react-router/dom 版は test 環境で二重読込になる)。
  const pendingCorrection = useRef<{
    search: string
    selection: Pick<DetailFilterState, 'bgCode' | 'retailerCode'>
  } | null>(null)
  useEffect(() => {
    pendingCorrection.current = null
  }, [search])
  const correctedSelection = useCallback(
    (): Pick<DetailFilterState, 'bgCode' | 'retailerCode'> =>
      pendingCorrection.current?.search === search
        ? pendingCorrection.current.selection
        : { bgCode: filter.bgCode, retailerCode: filter.retailerCode },
    [search, filter],
  )

  const navigate = useCallback(
    (next: DetailUrlState, replace: boolean): void => {
      const nextSearch = writeDetailUrlState(
        new URLSearchParams(search),
        next,
      ).toString()
      if (nextSearch === search) return
      setSearchParams(new URLSearchParams(nextSearch), { replace })
    },
    [search, setSearchParams],
  )

  const changeDate = useCallback(
    (date: Date): void =>
      navigate(
        { ...filter, ...correctedSelection(), displayDate: date },
        false,
      ),
    [navigate, filter, correctedSelection],
  )

  const goToToday = useCallback(
    (): void => changeDate(startOfTodayJst()),
    [changeDate],
  )

  const changeFilter = useCallback(
    (next: DetailFilterState): void => {
      const bgChanged =
        next.bgCode !== filter.bgCode ||
        next.retailerCode !== filter.retailerCode
      const togglesChanged = hasToggleChange(filter, next)
      let stored = readStored()
      if (bgChanged) {
        stored = withAreaBgSelection(stored, areaCode, next)
      }
      if (togglesChanged) {
        stored = withToggles(stored, next)
      }
      if (bgChanged || togglesChanged) writeStored(stored)
      if (
        next.demandGroup !== filter.demandGroup ||
        next.ratePlan !== filter.ratePlan
      ) {
        setPageOnly({ demandGroup: next.demandGroup, ratePlan: next.ratePlan })
      }
      // BG・小売に触れていない操作 (トグル等) は、補正待ちの選択を引き継ぐ。
      const selection = bgChanged ? next : correctedSelection()
      navigate(
        {
          ...next,
          bgCode: selection.bgCode,
          retailerCode: selection.retailerCode,
          displayDate,
        },
        !bgChanged,
      )
    },
    [areaCode, filter, navigate, displayDate, correctedSelection],
  )

  const correctFilter = useCallback(
    (next: DetailFilterState): void => {
      pendingCorrection.current = {
        search,
        selection: { bgCode: next.bgCode, retailerCode: next.retailerCode },
      }
      navigate({ ...next, displayDate }, true)
    },
    [navigate, displayDate, search],
  )

  return {
    displayDate,
    filter,
    changeDate,
    goToToday,
    changeFilter,
    correctFilter,
  }
}
